// Phase 1C — camera controller math + spatial hash. Pure functions; the
// runtime component wraps a mutable Camera in a ref and calls these.
import type { Parcel } from "./types";

export interface Camera {
  x: number; // world coord at CSS-pixel (0,0)
  y: number;
  scale: number; // CSS pixels per world unit
}

// screen (CSS px) -> world
export function screenToWorld(cam: Camera, sx: number, sy: number): { x: number; y: number } {
  return { x: cam.x + sx / cam.scale, y: cam.y + sy / cam.scale };
}

// world -> screen (CSS px)
export function worldToScreen(cam: Camera, wx: number, wy: number): { x: number; y: number } {
  return { x: (wx - cam.x) * cam.scale, y: (wy - cam.y) * cam.scale };
}

// Fit the entire 1000x1000 world into a CSS viewport (with a small margin).
// WO#5 v2 §TERRITORY-BLEED — margin lowered from 0.05 → 0.02 so the world
// nearly fills the viewport and stops feeling like a small square in a
// black sea. The change is purely presentational; all callers still get a
// valid `Camera` and the world remains fully visible (regression test
// `phase1c/camera.test.ts::fitGrid` still holds at 0.02).
export function fitGrid(viewportW: number, viewportH: number, worldSize = 1000, margin = 0.02): Camera {
  const usable = Math.min(viewportW, viewportH) * (1 - margin * 2);
  const scale = usable / worldSize;
  // Center world in viewport
  const worldPixels = worldSize * scale;
  const offsetX = (viewportW - worldPixels) / 2;
  const offsetY = (viewportH - worldPixels) / 2;
  return { x: -offsetX / scale, y: -offsetY / scale, scale };
}

// Zoom around a screen anchor keeping the world point under the anchor fixed.
export function zoomAt(
  cam: Camera,
  anchorSx: number,
  anchorSy: number,
  newScale: number,
): Camera {
  const w = screenToWorld(cam, anchorSx, anchorSy);
  const next: Camera = { ...cam, scale: newScale };
  // Solve for x,y so worldToScreen(next, w.x, w.y) === (anchorSx, anchorSy)
  next.x = w.x - anchorSx / newScale;
  next.y = w.y - anchorSy / newScale;
  return next;
}

// H2.3 WO#5 §MAX_ZOOM CAP — Product-level ceiling above which the Grid
// stops feeling like a territory/map and starts feeling like a single-
// cell pixel editor. At scale=15, one world cell = 15 CSS pixels; on a
// 1920×1080 canvas ~128×72 cells remain visible → strong spatial
// context preserved. Mobile (390-wide) still surfaces ~26 cells wide.
// This ceiling clamps EVERY user-accessible zoom path (wheel, pinch,
// zoom buttons) through the single canonical `clampCamera`. LOD band
// thresholds (OVERVIEW 4.27 / DETAIL 7.0) are UNCHANGED — only the
// ceiling is raised, so scenario appearance/procedures stay identical.
export const MAX_ZOOM = 15;

// Clamp camera so at least SOME of the world is visible. Simple guard.
export function clampCamera(
  cam: Camera,
  viewportW: number,
  viewportH: number,
  worldSize = 1000,
  minScale = 0.01,
  maxScale = MAX_ZOOM,
): Camera {
  const scale = Math.max(minScale, Math.min(maxScale, cam.scale));
  const visW = viewportW / scale;
  const visH = viewportH / scale;
  // Keep camera within [-visW, worldSize] etc. (loose but sufficient)
  const x = Math.max(-visW * 0.5, Math.min(worldSize - visW * 0.5, cam.x));
  const y = Math.max(-visH * 0.5, Math.min(worldSize - visH * 0.5, cam.y));
  return { x, y, scale };
}

// ---- Spatial hash / uniform bucket grid over world coords ----
// A parcel is registered in every bucket it intersects (half-open geometry).
// Screen->world hit-test then only checks that bucket's candidates.

export interface SpatialHash {
  cellSize: number;
  worldSize: number;
  buckets: Map<string, Parcel[]>;
}

export function buildSpatialHash(parcels: Parcel[], cellSize = 32, worldSize = 1000): SpatialHash {
  const buckets = new Map<string, Parcel[]>();
  for (const p of parcels) {
    const bx0 = Math.floor(p.x / cellSize);
    const by0 = Math.floor(p.y / cellSize);
    const bx1 = Math.floor((p.x + p.w - 1) / cellSize);
    const by1 = Math.floor((p.y + p.h - 1) / cellSize);
    for (let by = by0; by <= by1; by++) {
      for (let bx = bx0; bx <= bx1; bx++) {
        const k = `${bx},${by}`;
        let arr = buckets.get(k);
        if (!arr) {
          arr = [];
          buckets.set(k, arr);
        }
        arr.push(p);
      }
    }
  }
  return { cellSize, worldSize, buckets };
}

export interface HitResult {
  parcel: Parcel | null;
  conflict: boolean;
  candidates: Parcel[];
}

// Hit-test in WORLD coords using half-open geometry: px>=x, px<x+w, py>=y, py<y+h.
export function hitTest(hash: SpatialHash, wx: number, wy: number): HitResult {
  if (wx < 0 || wy < 0 || wx >= hash.worldSize || wy >= hash.worldSize) {
    return { parcel: null, conflict: false, candidates: [] };
  }
  const bx = Math.floor(wx / hash.cellSize);
  const by = Math.floor(wy / hash.cellSize);
  const bucket = hash.buckets.get(`${bx},${by}`);
  if (!bucket) return { parcel: null, conflict: false, candidates: [] };
  const hits: Parcel[] = [];
  for (const p of bucket) {
    if (wx >= p.x && wx < p.x + p.w && wy >= p.y && wy < p.y + p.h) {
      hits.push(p);
    }
  }
  if (hits.length === 0) return { parcel: null, conflict: false, candidates: [] };
  if (hits.length > 1) {
    // §11: do NOT arbitrary-pick; surface degraded/conflict state.
    return { parcel: null, conflict: true, candidates: hits };
  }
  return { parcel: hits[0], conflict: false, candidates: hits };
}

// H2.3 WO#5 v2 §OWNER-PALETTE — deterministic owner color constrained
// to the world's teal / cyan-blue / coral / warm-amber families so every
// owned parcel reads as belonging to THE GRID rather than random hue
// noise. Determinism preserved: same address → same color across runs
// and machines; the FNV-1a hash and the modulo-based band selection are
// pure functions of `owner.toLowerCase()`. Saturation and luminance
// ranges also honour the dark-UI legibility budget (mid-sat 42–63%,
// mid-lig 44–58%).
export function ownerColor(owner: string): string {
  const addr = owner.toLowerCase();
  // FNV-1a 32-bit
  let h = 2166136261;
  for (let i = 0; i < addr.length; i++) {
    h ^= addr.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  // 8-hue curated palette that harmonises with teal/coral shell.
  // Every owner deterministically maps to exactly one entry.
  const PALETTE: readonly [number, number, number][] = [
    [178, 62, 52], // teal
    [188, 58, 56], // aqua
    [200, 55, 55], // sky
    [220, 48, 58], // sapphire
    [258, 42, 60], // indigo
    [342, 58, 62], // rose
    [8,   66, 62], // coral
    [38,  55, 58], // amber (rare warm accent)
  ];
  const idx = (h >>> 0) % PALETTE.length;
  // Add a small per-address luminance jitter (±3) so nearby owners
  // in the same palette bucket remain distinguishable at parcel-fill
  // granularity, without breaking the palette identity.
  const jitter = (((h >>> 12) & 0x7) - 3);
  const [hue, sat, lig] = PALETTE[idx];
  return `hsl(${hue} ${sat}% ${Math.max(38, Math.min(66, lig + jitter))}%)`;
}
